import React, { useLayoutEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { Stack, Typography, StackProps, SxProps, useTheme, IconButton, Box } from "@mui/material";
import { Theme } from "@mui/material/styles";
import useSticky from "../../../Utils/Hooks/useSticky";
import { ArrowBackOutlined, ChevronLeftOutlined, ChevronRightOutlined } from "@mui/icons-material";

export interface ListPageHeaderProps extends StackProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  elevated?: boolean;
}

const ListPageHeader: React.FC<ListPageHeaderProps> = (props) => {
  const { children, sx, elevated = false, ...otherProps } = props;
  const theme = useTheme();
  const { ref, isSticky } = useSticky();

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      className={isSticky ? "sticky" : ""}
      spacing={4}
      ref={ref}
      sx={{
        backgroundColor: isSticky
          ? elevated
            ? theme.palette.background.paper
            : theme.palette.background.default
          : "transparent",
        position: "sticky",
        top: "-1px",
        zIndex: 2,
        mb: 1,
        boxShadow: isSticky ? theme.helpers(theme).stickyHighlights.boxShadow : "none",
        py: 2,
        ...sx,
      }}
      {...otherProps}
    >
      {children}
    </Stack>
  );
};

export default ListPageHeader;

export type ListPageHeaderLeftProps = {
  title?: string;
  subtitle?: string;
  backButton?: {
    to: string;
  };
} & ListPageHeaderProps;
const ListPageHeaderLeft: React.FC<ListPageHeaderLeftProps> = (props) => {
  const { title, subtitle, backButton, children, ...otherProps } = props;
  return (
    <Stack flex={1} overflow="hidden" direction="row" alignItems="flex-start" {...otherProps}>
      {backButton && (
        <IconButton component={RouterLink} to={backButton.to} size="small" color="ghost" sx={{ mr: 0.5 }}>
          <ArrowBackOutlined />
        </IconButton>
      )}
      {title && (
        <Stack direction="column">
          <Typography variant="h5" sx={(theme) => ({ flexShrink: 0, py: 0.75, mr: 2 })}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
      )}
      <Stack
        direction="row"
        alignSelf={"stretch"}
        alignItems="center"
        spacing={1}
        flexWrap="nowrap"
        sx={{ ".MuiTypography-h5": { py: 0.75 } }}
      >
        {children}
      </Stack>
    </Stack>
  );
};

export type ListPageHeaderRightProps = ListPageHeaderProps;
const ListPageHeaderRight: React.FC<ListPageHeaderRightProps> = (props) => {
  const { children, ...otherProps } = props;
  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" flexShrink={0} spacing={1} {...otherProps}>
      {children}
    </Stack>
  );
};

export type ListPageHeaderGroupActionsProps = ListPageHeaderProps;
const ListPageHeaderGroupActions: React.FC<ListPageHeaderGroupActionsProps> = (props) => {
  const { children, elevated = false, ...otherProps } = props;
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsOverflowing, setActionsOverflowing] = useState<boolean>(false);
  const [actionsExpanded, setActionsExpanded] = useState<boolean>(false);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      const el = actionsRef.current;
      if (el) {
        // For horizontal overflow (optional):
        const isHorizontallyOverflowing = el.scrollWidth > el.clientWidth;
        setActionsOverflowing(isHorizontallyOverflowing);
      }
    };

    // Check overflow when the component mounts
    checkOverflow();

    // Optional: Re-check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [actionsRef.current?.clientWidth]);

  return (
    <Stack
      direction="row"
      sx={(theme) => ({
        alignItems: "center",
        justifyContent: "flex-start",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: actionsExpanded ? actionsRef.current?.scrollWidth : "0",
        //flexBasis: actionsExpanded ? actionsRef.current?.scrollWidth : collapsedWidth,
        overflowX: "hidden",
        position: "relative",
        background: actionsExpanded
          ? elevated
            ? theme.palette.background.paper
            : theme.palette.background.default
          : "transparent",
        zIndex: actionsExpanded ? 2 : "unset",
        paddingRight: actionsOverflowing ? 8 : 0,
        left: 0,
        transition: theme.transitions.create("min-width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      })}
      ref={actionsRef}
      spacing={1}
      onMouseEnter={() => setActionsExpanded(true)}
      onMouseLeave={() => setActionsExpanded(false)}
      {...otherProps}
    >
      {children}
      {actionsOverflowing && (
        <Box
          sx={(theme) => ({
            background: elevated
              ? theme.helpers(theme).maskingGradient.backgroundElevated
              : theme.helpers(theme).maskingGradient.backgroundDefault,
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            right: 0,
            pl: 2,
          })}
        >
          <IconButton size="small" color="ghost" onClick={() => setActionsExpanded(!actionsExpanded)}>
            {actionsExpanded ? <ChevronLeftOutlined /> : <ChevronRightOutlined />}
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};

export { ListPageHeaderLeft, ListPageHeaderRight, ListPageHeaderGroupActions };
